import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowLeft, ArrowRight, Check, ShieldAlert, Loader2, Sparkles, Copy, FilePlus2,
  CheckCircle2, Circle, Clock, Mail, MessageSquare, Rocket, FileText, Lock,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { PageTransition } from '@/components/PageTransition'
import { useCadence } from '@/contexts/CadenceContext'
import { usePermissions } from '@/hooks/usePermissions'
import { supabase } from '@/integrations/supabase/client'
import { STEP_TYPE_CONFIG, type CadenceStep } from '@/types/cadence'
import { cn } from '@/lib/utils'

// The shipped "Chief Outreach 9-day" cadence in the Yuno org — the proven
// template a brand-new cadence clones from so the owner never starts blank.
// duplicateCadence reads the source by id (org members can SELECT it via RLS)
// and copies steps + policy config onto the new, current-user-owned cadence.
const TEMPLATE_CADENCE_ID = '48c6b5bc-0276-47bb-a275-5fa8beaa6c30'
const TEMPLATE_LABEL = 'Chief Outreach 9-day'

type Origin = 'template' | 'duplicate' | 'blank'

// ────────────────────────────────────────────────────────────────────────────
// NewAutomCadence — 3-step wizard so a new cadence is never born blank:
//   1) choose an origin (proven template · duplicate an existing one · blank)
//   2) review the inherited steps + name it
//   3) create as draft or activate → navigate to /autom-cadence/<newId>
// Template/duplicate reuse CadenceContext.duplicateCadence (which now also clones
// the Carlos rubric / touch angles / circuit-breaker config); blank uses
// createCadence. The whole page is gated by outreach_admin, once, like the shell.
// ────────────────────────────────────────────────────────────────────────────
export function NewAutomCadence() {
  const navigate = useNavigate()
  const { cadences, isLoading, createCadence, duplicateCadence, updateCadence } = useCadence()
  const { outreach_admin } = usePermissions()

  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [origin, setOrigin] = useState<Origin | null>(null)
  const [duplicateSourceId, setDuplicateSourceId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [nameTouched, setNameTouched] = useState(false)
  const [activateOnCreate, setActivateOnCreate] = useState(false)
  const [creating, setCreating] = useState(false)

  // The concrete source cadence id (template or the picked duplicate). null for blank.
  const sourceId = useMemo(() => {
    if (origin === 'template') return TEMPLATE_CADENCE_ID
    if (origin === 'duplicate') return duplicateSourceId
    return null
  }, [origin, duplicateSourceId])

  // ── Template legibility probe ───────────────────────────────────────────────
  // The proven template lives in the Yuno org. For an admin of ANY other org the
  // RLS on cadences / cadence_steps blocks reading it, so the preview would lie
  // ("no tiene pasos configurados") and duplicateCadence would fail at Create
  // ("Source cadence not found"). Probe it eagerly (independent of the chosen
  // origin) so we can decide the default origin, the "Recomendado" badge and the
  // Create button up front. A cross-org read returns rows=[] with NO error, so
  // "read OK but 0 rows" is the illegible signal — same as an outright error.
  const templateInContext = cadences.some((c) => c.id === TEMPLATE_CADENCE_ID)
  const { data: templateStepCount, isLoading: templateProbeLoading, isError: templateProbeError } = useQuery({
    queryKey: ['autom-cadence-template-probe', TEMPLATE_CADENCE_ID],
    queryFn: async (): Promise<number> => {
      const local = cadences.find((c) => c.id === TEMPLATE_CADENCE_ID)
      if (local?.steps && local.steps.length > 0) return local.steps.length
      const { count, error } = await supabase
        .from('cadence_steps')
        .select('id', { count: 'exact', head: true })
        .eq('cadence_id', TEMPLATE_CADENCE_ID)
      if (error) throw error
      return count ?? 0
    },
    // Only meaningful once the context cadences have loaded (so templateInContext
    // is reliable); until then treat legibility as 'unknown'.
    enabled: !isLoading,
  })

  // Tri-state: null while the probe is in flight (don't flash a wrong default),
  // true = readable in the user's org, false = blocked by RLS (cross-org).
  const templateReadable: boolean | null = useMemo(() => {
    if (templateInContext) return true
    if (templateProbeLoading) return null
    if (templateProbeError) return false
    if (typeof templateStepCount !== 'number') return null
    return templateStepCount > 0
  }, [templateInContext, templateProbeLoading, templateProbeError, templateStepCount])

  const hasOwnCadences = cadences.length > 0

  // Preview the steps the new cadence will inherit. For a duplicate the source is
  // usually already in the in-context list, but the template (owned by whoever
  // shipped it) may not be — so read steps straight from the DB, keyed by source.
  const { data: sourceSteps, isLoading: stepsLoading, isError: stepsError } = useQuery({
    queryKey: ['autom-cadence-source-steps', sourceId],
    queryFn: async (): Promise<CadenceStep[]> => {
      if (!sourceId) return []
      const local = cadences.find((c) => c.id === sourceId)
      if (local?.steps && local.steps.length > 0) return local.steps
      const { data, error } = await supabase
        .from('cadence_steps')
        .select('*')
        .eq('cadence_id', sourceId)
      if (error) throw error
      return (data ?? []) as CadenceStep[]
    },
    enabled: !!sourceId,
  })

  // When the source is the illegible template, the steps read returns rows=[]
  // (RLS filters silently, no error). Whether the read errored or came back empty
  // cross-org, "we could not actually read the source" is what matters — the copy
  // and the Create gate must reflect that, not promise "se copiarán al crear".
  const sourceIsUnreadableTemplate =
    origin === 'template' && templateReadable === false

  // Pick the default origin once we know whether the template is legible — the
  // wizard "never starts blank". If the template is readable → recommend it. If
  // RLS blocks it (cross-org) → fall back to "Duplicar una existente" when the
  // user has own cadences, else "En blanco". Only ever set the default while no
  // origin is chosen yet, so we never fight the user's own selection.
  const [defaultApplied, setDefaultApplied] = useState(false)
  useEffect(() => {
    if (defaultApplied || origin !== null || templateReadable === null) return
    if (templateReadable) {
      setOrigin('template')
    } else {
      setOrigin(hasOwnCadences ? 'duplicate' : 'blank')
    }
    setDefaultApplied(true)
  }, [defaultApplied, origin, templateReadable, hasOwnCadences])

  const sortedSteps = useMemo(() => {
    return [...(sourceSteps ?? [])].sort((a, b) => {
      if (a.day_offset !== b.day_offset) return a.day_offset - b.day_offset
      return a.order_in_day - b.order_in_day
    })
  }, [sourceSteps])

  // Suggested default name (the user can override). Never leave it blank.
  const suggestedName = useMemo(() => {
    if (origin === 'template') return `${TEMPLATE_LABEL} (copia)`
    if (origin === 'duplicate' && duplicateSourceId) {
      const src = cadences.find((c) => c.id === duplicateSourceId)
      return src ? `${src.name} (copia)` : 'Cadencia nueva'
    }
    return 'Cadencia nueva'
  }, [origin, duplicateSourceId, cadences])

  const effectiveName = nameTouched ? name : suggestedName

  // ── Access guard (applied once for the whole page) ──────────────────────────
  if (!outreach_admin) {
    return (
      <PageTransition className="p-8">
        <div className="max-w-md mx-auto text-center py-16">
          <ShieldAlert className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <h1 className="text-lg font-semibold">Acceso restringido</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Solo un administrador puede crear cadencias automatizadas.
          </p>
          <Button asChild variant="outline" size="sm" className="mt-4">
            <Link to="/autom-cadence"><ArrowLeft className="h-4 w-4 mr-1" />Volver a Autom Cadence</Link>
          </Button>
        </div>
      </PageTransition>
    )
  }

  const canContinueFromStep1 =
    (origin === 'template' && templateReadable === true) ||
    origin === 'blank' ||
    (origin === 'duplicate' && !!duplicateSourceId)

  async function handleCreate() {
    if (creating) return
    // Hard stop: never let Create run the template path when the template is not
    // readable in the user's org — duplicateCadence would fail server-side with
    // "Source cadence not found". This mirrors the disabled Create button; belt
    // and suspenders in case state changed between render and click.
    if (sourceIsUnreadableTemplate) {
      toast.error('Esta plantilla no está disponible para tu organización. Duplica una cadencia propia o parte en blanco.')
      return
    }
    const finalName = effectiveName.trim()
    if (!finalName) {
      toast.error('Ponle un nombre a la cadencia')
      return
    }
    setCreating(true)
    try {
      let newId: string | null = null

      if (origin === 'blank') {
        const created = await createCadence(finalName)
        if (!created) throw new Error('No se pudo crear la cadencia')
        newId = created.id
        if (activateOnCreate) {
          await updateCadence(created.id, { status: 'active' })
        }
      } else {
        if (!sourceId) throw new Error('No hay cadencia de origen seleccionada')
        // duplicateCadence copies steps + clones the policy config (Carlos rubric,
        // touch angles, circuit breaker) onto the new cadence. The policy clone is
        // best-effort: created.policyCloned tells us whether it succeeded.
        const created = await duplicateCadence(sourceId)
        if (!created) throw new Error('No se pudo duplicar la cadencia de origen')
        newId = created.id
        // The policy clone failed silently server-side — the cadence + steps are
        // fine, but Política / Ángulos / Límites fell back to the org defaults.
        // Warn so the user reviews them instead of shipping wrong policy blindly.
        if (!created.policyCloned) {
          toast.warning(
            'Se creó la cadencia, pero no se pudo copiar la política — revísala en Política/Ángulos/Límites',
          )
        }
        // duplicateCadence auto-names "<base> (N)"; honor the name the user chose
        // (and the activate choice) in one update.
        const patch: { name?: string; status?: 'active' } = {}
        if (finalName && finalName !== created.name) patch.name = finalName
        if (activateOnCreate) patch.status = 'active'
        if (Object.keys(patch).length > 0) {
          await updateCadence(created.id, patch)
        }
      }

      toast.success(
        activateOnCreate ? 'Cadencia creada y activada' : 'Cadencia creada como borrador',
      )
      navigate(`/autom-cadence/${newId}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'No se pudo crear la cadencia')
      setCreating(false)
    }
  }

  return (
    <PageTransition className="p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* ── Header + stepper ─────────────────────────────────────────────── */}
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2 text-muted-foreground">
            <Link to="/autom-cadence"><ArrowLeft className="h-4 w-4 mr-1" />Autom Cadence</Link>
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">Nueva cadencia</h1>
          <p className="mt-1 text-sm text-muted-foreground max-w-2xl">
            Empieza desde una plantilla probada, duplica una cadencia existente o parte en blanco —
            nunca desde cero. Heredarás sus pasos y su política de Carlos, ángulos y límites.
          </p>
        </div>

        <Stepper current={step} />

        {/* ── Step 1: choose origin ────────────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-4">
            <OriginCard
              selected={origin === 'template' && templateReadable === true}
              onSelect={() => setOrigin('template')}
              disabled={templateReadable === false}
              icon={templateReadable === false ? <Lock className="size-5" /> : <Sparkles className="size-5" />}
              title="Plantilla probada"
              desc={
                templateReadable === false
                  ? `La cadencia ${TEMPLATE_LABEL} pertenece a otra organización, así que no está disponible para la tuya. Duplica una cadencia propia o parte en blanco.`
                  : `Clona la cadencia ${TEMPLATE_LABEL}: 9 días de pasos LinkedIn + email ya calibrados, con la política de Carlos y los ángulos por touch.`
              }
              badge={
                templateReadable === false
                  ? 'No disponible en tu organización'
                  : templateReadable === true
                    ? 'Recomendado'
                    : undefined
              }
            />
            <OriginCard
              selected={origin === 'duplicate'}
              onSelect={() => setOrigin('duplicate')}
              icon={<Copy className="size-5" />}
              title="Duplicar una cadencia existente"
              desc="Copia los pasos y la configuración de una de tus cadencias como punto de partida."
            >
              {origin === 'duplicate' && (
                <div className="mt-3 space-y-2">
                  {isLoading && cadences.length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="size-4 animate-spin" /> Cargando cadencias…
                    </div>
                  ) : cadences.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No tienes cadencias para duplicar. Usa una plantilla o parte en blanco.
                    </p>
                  ) : (
                    <div className="grid gap-1.5 max-h-56 overflow-y-auto pr-1">
                      {cadences.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setDuplicateSourceId(c.id) }}
                          className={cn(
                            'flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm transition-colors',
                            duplicateSourceId === c.id
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:bg-muted/50',
                          )}
                        >
                          <span className="truncate font-medium">{c.name}</span>
                          <span className="ml-2 flex shrink-0 items-center gap-2">
                            <Badge variant="secondary" className="text-[10px]">
                              {(c.steps?.length ?? 0)} pasos
                            </Badge>
                            <Badge
                              variant={c.status === 'active' ? 'default' : 'secondary'}
                              className="uppercase text-[10px] tracking-wide"
                            >
                              {c.status === 'active' ? 'activa' : 'borrador'}
                            </Badge>
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </OriginCard>
            <OriginCard
              selected={origin === 'blank'}
              onSelect={() => setOrigin('blank')}
              icon={<FilePlus2 className="size-5" />}
              title="En blanco"
              desc="Crea una cadencia vacía y arma los pasos tú mismo en el builder. Sin política heredada."
            />

            <div className="flex justify-end pt-2">
              <Button disabled={!canContinueFromStep1} onClick={() => setStep(2)}>
                Continuar <ArrowRight className="ml-1.5 size-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2: review inherited steps + name ────────────────────────── */}
        {step === 2 && (
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="cadence-name">Nombre de la cadencia</Label>
              <Input
                id="cadence-name"
                value={effectiveName}
                onChange={(e) => { setNameTouched(true); setName(e.target.value) }}
                placeholder="Ej. Outreach enterprise LATAM"
                maxLength={120}
              />
            </div>

            <Separator />

            <div>
              <h2 className="text-sm font-semibold">
                {origin === 'blank' ? 'Pasos' : 'Pasos que heredará'}
              </h2>
              {origin === 'blank' ? (
                <p className="mt-1 text-sm text-muted-foreground">
                  Empezarás sin pasos. Los agregarás uno a uno en el builder visual de la cadencia.
                </p>
              ) : sourceIsUnreadableTemplate ? (
                // Cross-org: the template is not readable here. Do NOT promise
                // "se copiarán al crear" (it can't) — Create would fail with
                // "Source cadence not found". Tell the truth and point them at
                // the two paths that DO work.
                <p className="mt-2 text-sm text-destructive">
                  Esta plantilla pertenece a otra organización y no está disponible para la tuya, así
                  que no se pueden copiar sus pasos. Vuelve atrás y duplica una cadencia propia o parte
                  en blanco.
                </p>
              ) : stepsLoading ? (
                <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" /> Cargando pasos de la cadencia de origen…
                </div>
              ) : stepsError ? (
                // A read error on a source we SHOULD be able to read (same org):
                // the copy runs server-side from the source id, so the promise
                // holds. The illegible-template case is handled above.
                <p className="mt-2 text-sm text-destructive">
                  No se pudieron cargar los pasos de la cadencia de origen. Puedes continuar de
                  todos modos — se copiarán al crear.
                </p>
              ) : sortedSteps.length === 0 ? (
                <p className="mt-2 text-sm text-muted-foreground">
                  La cadencia de origen no tiene pasos configurados.
                </p>
              ) : (
                <div className="mt-3 space-y-2">
                  {sortedSteps.map((s) => (
                    <StepPreviewRow key={s.id} step={s} />
                  ))}
                  <p className="pt-1 text-xs text-muted-foreground">
                    También se copiará la política de Carlos, los ángulos por touch y los límites
                    operativos de la cadencia de origen.
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(1)}>
                <ArrowLeft className="mr-1.5 size-4" /> Atrás
              </Button>
              <Button onClick={() => setStep(3)}>
                Continuar <ArrowRight className="ml-1.5 size-4" />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 3: draft vs activate + create ───────────────────────────── */}
        {step === 3 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-sm font-semibold">¿Cómo quieres crearla?</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Aunque la actives, nada se envía hasta que el motor de outreach de la organización
                esté encendido.
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <OriginCard
                  selected={!activateOnCreate}
                  onSelect={() => setActivateOnCreate(false)}
                  icon={<FileText className="size-5" />}
                  title="Como borrador"
                  desc="Créala pausada para seguir configurándola antes de activarla."
                />
                <OriginCard
                  selected={activateOnCreate}
                  onSelect={() => setActivateOnCreate(true)}
                  icon={<Rocket className="size-5" />}
                  title="Activar ahora"
                  desc="Márcala como activa. Podrás asignarle leads de inmediato."
                />
              </div>
            </div>

            <Card className="bg-muted/30 p-4">
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
                <dt className="text-muted-foreground">Origen</dt>
                <dd className="font-medium">
                  {origin === 'template' && `Plantilla ${TEMPLATE_LABEL}`}
                  {origin === 'duplicate' &&
                    `Duplicado de ${cadences.find((c) => c.id === duplicateSourceId)?.name ?? '—'}`}
                  {origin === 'blank' && 'En blanco'}
                </dd>
                <dt className="text-muted-foreground">Nombre</dt>
                <dd className="font-medium truncate">{effectiveName.trim() || '—'}</dd>
                <dt className="text-muted-foreground">Pasos</dt>
                <dd className="font-medium">
                  {origin === 'blank' ? '0 (los agregarás en el builder)' : `${sortedSteps.length}`}
                </dd>
                <dt className="text-muted-foreground">Estado</dt>
                <dd className="font-medium">{activateOnCreate ? 'Activa' : 'Borrador'}</dd>
              </dl>
            </Card>

            {sourceIsUnreadableTemplate && (
              <p className="text-sm text-destructive">
                Esta plantilla no está disponible para tu organización, así que no se puede crear la
                cadencia desde ella. Vuelve atrás y duplica una cadencia propia o parte en blanco.
              </p>
            )}

            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(2)} disabled={creating}>
                <ArrowLeft className="mr-1.5 size-4" /> Atrás
              </Button>
              <Button onClick={handleCreate} disabled={creating || sourceIsUnreadableTemplate}>
                {creating ? (
                  <><Loader2 className="mr-1.5 size-4 animate-spin" /> Creando…</>
                ) : (
                  <><Check className="mr-1.5 size-4" /> Crear cadencia</>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageTransition>
  )
}

// ── Stepper -------------------------------------------------------------------
function Stepper({ current }: { current: 1 | 2 | 3 }) {
  const steps = [
    { n: 1, label: 'Origen' },
    { n: 2, label: 'Revisar' },
    { n: 3, label: 'Crear' },
  ] as const
  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => {
        const done = current > s.n
        const active = current === s.n
        return (
          <div key={s.n} className="flex items-center gap-2">
            <div
              className={cn(
                'flex items-center gap-2 rounded-full px-3 py-1 text-sm transition-colors',
                active && 'bg-primary text-primary-foreground',
                done && 'bg-primary/10 text-primary',
                !active && !done && 'bg-muted text-muted-foreground',
              )}
            >
              {done ? <Check className="size-3.5" /> : <span className="font-semibold">{s.n}</span>}
              <span className="font-medium">{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className="h-px w-6 bg-border" />}
          </div>
        )
      })}
    </div>
  )
}

// ── Selectable card (origin picker / draft-vs-activate) -----------------------
function OriginCard({
  selected, onSelect, icon, title, desc, badge, children, disabled = false,
}: {
  selected: boolean
  onSelect: () => void
  icon: React.ReactNode
  title: string
  desc: string
  badge?: string
  children?: React.ReactNode
  // When disabled the card is not selectable (e.g. a template the current org
  // can't read). It stays visible + explains itself, but can't become the origin.
  disabled?: boolean
}) {
  return (
    <Card
      role="button"
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onSelect() } }}
      className={cn(
        'p-4 transition-colors',
        disabled
          ? 'cursor-not-allowed opacity-60'
          : cn('cursor-pointer', selected ? 'border-primary ring-1 ring-primary' : 'hover:bg-muted/40'),
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md',
            selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
          )}
        >
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">{title}</h3>
            {badge && (
              <Badge variant={disabled ? 'secondary' : 'default'} className="text-[10px]">
                {badge}
              </Badge>
            )}
            {disabled ? (
              <Lock className="ml-auto size-4 text-muted-foreground/60" />
            ) : selected ? (
              <CheckCircle2 className="ml-auto size-5 text-primary" />
            ) : (
              <Circle className="ml-auto size-5 text-muted-foreground/40" />
            )}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
          {children}
        </div>
      </div>
    </Card>
  )
}

// ── Read-only inherited-step row ----------------------------------------------
function StepPreviewRow({ step }: { step: CadenceStep }) {
  const cfg = STEP_TYPE_CONFIG[step.step_type]
  const label = step.step_label || cfg?.label || step.step_type
  const Icon = cfg?.channel === 'email' ? Mail : cfg?.channel === 'linkedin' ? MessageSquare : Clock
  return (
    <div className="flex items-center gap-3 rounded-md border px-3 py-2">
      <Badge variant="secondary" className="shrink-0 tabular-nums">
        Día {step.day_offset}
      </Badge>
      <Icon className="size-4 shrink-0 text-muted-foreground" />
      <span className="truncate text-sm font-medium">{label}</span>
      <span className="ml-auto shrink-0 text-xs text-muted-foreground">
        {cfg?.label ?? step.step_type}
      </span>
    </div>
  )
}
